<template>
  <v-card
      elevation="3"
      class="ma-4"
      :loading="loading"
  >
    <v-toolbar
        flat
        color="white"
    >
      <h3>Hospital Info</h3>
      <v-spacer />
      <v-btn
          v-if="$can('edit', 'hospitalProfile')"
          color="error"
          @click="$emit('edit')"
      >
        <v-icon
            left
            dark
        >
          mdi-pencil-outline
        </v-icon>
        Edit
      </v-btn>
    </v-toolbar>

    <v-card-text v-if="hospital">
      <v-row>
        <v-col
            sm="4"
            class="info-col"
        >
          <v-row>
            <v-col sm="12"><b>Name:</b> {{ hospital.name }}
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="12"><b>Address:</b><br />
              {{ hospital.address }}<br />
              {{ hospital.city }}, {{ hospital.stateID }} {{ hospital.zip }}
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="12">
              <b>Phone:</b> {{ hospital.phone }}
            </v-col>
          </v-row>

          <v-row v-if="hospital.fax">
            <v-col sm="12">
              <b>Fax:</b> {{ hospital.fax }}
            </v-col>
          </v-row>

          <v-row>
            <v-col sm="12">
              <b>Email:</b> {{ hospital.email }}
            </v-col>
          </v-row>

          <v-row v-if="typeNames.length">
            <v-col sm="12">
              <b>{{ $t('hospitals.editPopup.types') }}:</b> {{ typeNames.join(', ') }}
            </v-col>
          </v-row>

          <v-row v-if="traumaTypes.length">
            <v-col sm="12">
              <b>{{ $t('hospitals.editPopup.traumaPopulations') }}:</b>
              <div
                  v-for="traumaType in traumaTypes"
                  :key="traumaType.name"
              >
                <b>{{ traumaType.name }}:</b> {{ traumaType.populations }}
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col sm="12">
              <b>{{ $t('hospitals.editPopup.baseHospital') }}:</b>
              {{ hospital.baseHospital ? $t('general.yes') : $t('general.no') }}
            </v-col>
          </v-row>
        </v-col>
        <v-col sm="8">
          <div
              ref="googleMap"
              class="google-map"
              v-bind:style="{ height: '400px' }"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script>
import { mapState } from 'vuex';
import { FETCH_DATA } from '@/store/HospitalsTypes/actions';

const TRAUMA_CAPABILITY_TYPE = 'T';

/* eslint-disable no-undef */
export default {
  name: 'HospitalInfo',

  props: {
    hospital: {
      type: Object,
      required: false,
      default: null,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      map: null,
      marker: null,
    };
  },

  computed: {
    ...mapState('hospitalsTypes', ['hospitalsTypes']),

    /**
     * Names of the hospital types.
     */
    typeNames() {
      return this.hospitalTypes.map(type => this.typeName(type.typeID)).filter(Boolean);
    },

    /**
     * Trauma types with the supported patient populations.
     */
    traumaTypes() {
      return this.hospitalTypes
        .filter(type => this.isTraumaType(type.typeID))
        .map(type => ({
          name: this.typeName(type.typeID),
          populations: [
            type.supportsAdult ? this.$t('hospitals.editPopup.supportsAdult') : null,
            type.supportsPediatric ? this.$t('hospitals.editPopup.supportsPediatric') : null,
          ].filter(Boolean).join(', '),
        }));
    },

    /**
     * Hospital types with the pivot flags.
     */
    hospitalTypes() {
      return (this.hospital && this.hospital.types) || [];
    },
  },

  created() {
    this.$store.dispatch(`hospitalsTypes/${FETCH_DATA}`);
  },

  watch: {
    /**
     * Watch pagination options and update pagination props.
     */
    hospital() {
      if (this.hospital) {
        this.$nextTick(async () => {
          this.initMap();
        });
      }
    },
  },

  methods: {
    /**
     * Check whether the hospital type has the trauma capability.
     *
     * @param {Number} typeID - Hospital type identifier.
     *
     * @return {Boolean} True if the type is a trauma type.
     */
    isTraumaType(typeID) {
      const type = this.hospitalsTypes.find(el => el.id === typeID);

      return !!type && type.capabilityType === TRAUMA_CAPABILITY_TYPE;
    },

    /**
     * Get the type name by identifier.
     *
     * @param {Number} typeID - Hospital type identifier.
     *
     * @return {String} Type name.
     */
    typeName(typeID) {
      const type = this.hospitalsTypes.find(el => el.id === typeID);

      return type ? type.name : '';
    },

    initMap() {
      const latLng = new google.maps.LatLng(this.hospital.latitude, this.hospital.longitude);

      this.map = new google.maps.Map(this.$refs.googleMap, {
        zoom: 15,
        center: latLng,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
      });

      this.marker = new google.maps.Marker({
        position: latLng,
        map: this.map,
        title: this.hospital.name,
      });
    },
  },
};
</script>
<style lang="scss">
.google-map {
  width: 100%;
}

.info-col .row {
  margin-top: 0;
  margin-bottom: 12px;
}

.info-col .row .col {
  padding-top: 2px;
  padding-bottom: 2px;
}
</style>
